// Batch-06: app-wide notification store. Holds the bell's unread count and list,
// fed by the REST notification APIs plus realtime SignalR pushes on the presence hub.
// SignalR is a convenience; the HTTP APIs are the source of truth (used on load/reconnect).
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { notification } from 'antd'
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from 'src/Services/NotificationService'
import {
  subscribeToNotifications,
  subscribeToPresenceReconnect,
} from 'src/Hooks/CounselorHooks/SingleR/useSignalRPresence'

const NotificationContext = createContext()

const isAuthed = () => Boolean(localStorage.getItem('token'))

export const NotificationProvider = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  // dedupe realtime pushes by recipientId (read state belongs to the recipient)
  const seenRef = useRef(new Set())
  const hasLoadedRef = useRef(false)

  const refreshCount = useCallback(async () => {
    if (!isAuthed()) return
    try {
      setUnreadCount(await getUnreadCount())
    } catch (e) {
      // keep last known count on transient errors
    }
  }, [])

  const loadList = useCallback(async () => {
    if (!isAuthed()) return
    setLoading(true)
    try {
      const data = await getNotifications({ pageNumber: 1, pageSize: 20 })
      const list = Array.isArray(data) ? data : data?.items ?? []
      seenRef.current = new Set(list.map((n) => n.recipientId))
      setItems(list)
      setHasLoaded(true)
      hasLoadedRef.current = true
    } catch (e) {
      // leave existing items on error
    } finally {
      setLoading(false)
    }
  }, [])

  const markRead = useCallback(
    async (recipientId) => {
      if (recipientId == null) return
      // optimistic; PATCH is idempotent
      setItems((prev) =>
        prev.map((n) => (n.recipientId === recipientId && !n.isRead ? { ...n, isRead: true } : n)),
      )
      setUnreadCount((c) => Math.max(0, c - 1))
      try {
        await markNotificationRead(recipientId)
      } catch (e) {
        refreshCount()
      }
    },
    [refreshCount],
  )

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })))
    setUnreadCount(0)
    try {
      await markAllNotificationsRead()
    } catch (e) {
      refreshCount()
    }
  }, [refreshCount])

  // Realtime: prepend new notifications, bump the unread count.
  useEffect(() => {
    const offNotify = subscribeToNotifications((n) => {
      if (!n || n.recipientId == null) return
      if (seenRef.current.has(n.recipientId)) return
      seenRef.current.add(n.recipientId)
      setItems((prev) => [n, ...prev])
      if (!n.isRead) setUnreadCount((c) => c + 1)
      // Visible cue when a new notification arrives while the user is already in the app,
      // so a silently-incrementing bell count is not missed (handoff: new-notification visibility).
      notification.open({
        message: n.title,
        description: n.message,
        placement: 'bottomRight',
        duration: 4,
      })
    })
    // On reconnect a SignalR event may have been missed — reconcile from the API.
    const offReconnect = subscribeToPresenceReconnect(() => {
      refreshCount()
      if (hasLoadedRef.current) loadList()
    })
    return () => {
      offNotify()
      offReconnect()
    }
  }, [refreshCount, loadList])

  // Initial unread count once per authenticated session.
  useEffect(() => {
    refreshCount()
  }, [refreshCount])

  return (
    <NotificationContext.Provider
      value={{
        unreadCount,
        items,
        loading,
        hasLoaded,
        loadList,
        markRead,
        markAllRead,
        refresh: refreshCount,
      }}
    >
      {children}
    </NotificationContext.Provider>
  )
}

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useNotifications = () => useContext(NotificationContext)

export default NotificationContext
